<template>
  <div class="contact-page">
    <Navbar :currentLang="currentLang" @change-lang="changeLang" />

    <!-- Hero -->
    <section class="contact-hero">
      <div class="contact-hero-inner">
        <div class="contact-hero-text" data-aos="fade-up">
          <span class="section-eyebrow">Contact Us</span>
          <h1 class="contact-hero-title">Get in Touch with<br>Our Team</h1>
          <p class="contact-hero-sub">
            Whether you're just starting out or scaling fast, our team is here to help. Reach out and we'll get back to you within 24 hours.
          </p>
        </div>
        <div class="contact-hero-visual" data-aos="fade-left">
          <div class="contact-visual-card">
            <div class="visual-avatar-group">
              <div class="visual-avatar" v-for="i in 4" :key="i" :style="{ background: avatarColors[i-1] }">
                {{ avatarInitials[i-1] }}
              </div>
            </div>
            <p class="visual-caption">Our team is ready to help you succeed</p>
            <div class="visual-stats">
              <div class="visual-stat">
                <span class="visual-stat-num">&lt;24h</span>
                <span class="visual-stat-label">Avg Response Time</span>
              </div>
              <div class="visual-stat">
                <span class="visual-stat-num">98%</span>
                <span class="visual-stat-label">Satisfaction Rate</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Form + Info -->
    <section class="contact-main">
      <div class="contact-main-inner">
        <!-- Form -->
        <div class="contact-form-wrapper" data-aos="fade-right">
          <div class="form-header">
            <h2>Send Us a Message</h2>
            <p>Fill out the form below and we'll get back to you soon.</p>
          </div>
          <form class="contact-form" @submit.prevent="handleSubmit">
            <div class="form-row">
              <div class="form-group">
                <label for="name">Your Name *</label>
                <input type="text" id="name" v-model="form.name" placeholder="John Doe" required>
              </div>
              <div class="form-group">
                <label for="email">Email Address *</label>
                <input type="email" id="email" v-model="form.email" placeholder="john@yourstore.com" required>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="company">Company</label>
                <input type="text" id="company" v-model="form.company" placeholder="Your Store Name">
              </div>
              <div class="form-group">
                <label for="phone">Phone Number</label>
                <input type="tel" id="phone" v-model="form.phone" placeholder="+1 (555) 123-4567">
              </div>
            </div>
            <div class="form-group">
              <label for="service">I'm interested in...</label>
              <select id="service" v-model="form.service">
                <option value="">Select a service</option>
                <option value="dropshipping">Dropshipping</option>
                <option value="warehousing">Warehousing & Fulfillment</option>
                <option value="logistics">Global Logistics</option>
                <option value="qc">Quality Control</option>
                <option value="branding">Branded Packaging</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="form-group">
              <label for="message">Message *</label>
              <textarea id="message" v-model="form.message" placeholder="Tell us about your business and how we can help..." rows="5" required></textarea>
            </div>
            <button type="submit" class="form-submit" :disabled="submitting">
              <span v-if="!submitting">Send Message</span>
              <span v-else>Sending...</span>
              <svg v-if="!submitting" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:18px;height:18px;margin-left:6px;"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
            </button>
            <p v-if="submitSuccess" class="form-success">✓ Message sent successfully! We'll be in touch within 24 hours.</p>
          </form>
        </div>

        <!-- Info Sidebar -->
        <div class="contact-info-sidebar" data-aos="fade-left">
          <!-- Contact Methods -->
          <div class="info-card">
            <h3>Contact Information</h3>
            <div class="info-item" v-for="(ci, i) in contactInfo" :key="i">
              <div class="info-icon" :style="{ background: ci.bg }">
                <i :class="ci.icon"></i>
              </div>
              <div>
                <div class="info-label">{{ ci.label }}</div>
                <div class="info-value">{{ ci.value }}</div>
              </div>
            </div>
          </div>

          <!-- Office Locations -->
          <div class="info-card">
            <h3>Our Offices</h3>
            <div class="office-card" v-for="(o, i) in offices" :key="i">
              <div class="office-flag">{{ o.flag }}</div>
              <div>
                <h4 class="office-city">{{ o.city }}</h4>
                <p class="office-address">{{ o.address }}</p>
              </div>
            </div>
          </div>

          <!-- Business Hours -->
          <div class="info-card">
            <h3>Business Hours</h3>
            <div class="hours-row" v-for="(h, i) in businessHours" :key="i">
              <span class="hours-day">{{ h.day }}</span>
              <span class="hours-time">{{ h.time }}</span>
            </div>
            <div class="hours-note">
              <i class="fa-solid fa-circle-info"></i>
              We support 24/7 for Enterprise clients
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Map / Locations -->
    <section class="locations-section">
      <div class="locations-inner" data-aos="fade-up">
        <div class="section-header">
          <span class="section-eyebrow">Global Presence</span>
          <h2 class="section-title">Serving Sellers Worldwide</h2>
          <p class="section-desc">With warehouses and offices across 3 countries, we're always close to your customers.</p>
        </div>
        <div class="locations-grid">
          <div class="location-card" v-for="(l, i) in locations" :key="i" data-aos="fade-up" :data-aos-delay="i * 80">
            <div class="location-map" :style="{ background: l.bg }">
              <div class="location-pin">
                <i class="fa-solid fa-location-dot"></i>
              </div>
            </div>
            <div class="location-body">
              <h4 class="location-city">{{ l.city }}</h4>
              <p class="location-address">{{ l.address }}</p>
              <div class="location-tags">
                <span class="location-tag" v-for="(t, j) in l.tags" :key="j">{{ t }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Teaser -->
    <section class="contact-faq">
      <div class="contact-faq-inner" data-aos="fade-up">
        <h2>Looking for Quick Answers?</h2>
        <p>Check our Help Center for instant answers to common questions.</p>
        <router-link to="/faq" class="btn-outline-dark">Visit Help Center</router-link>
      </div>
    </section>

    <Footer :currentLang="currentLang" />
  </div>
</template>

<script>
import Navbar from '../components/Navbar.vue'
import Footer from '../components/Footer.vue'

export default {
  name: 'Contact',
  components: { Navbar, Footer },
  data() {
    return {
      currentLang: 'en',
      submitting: false,
      submitSuccess: false,
      form: {
        name: '',
        email: '',
        company: '',
        phone: '',
        service: '',
        message: ''
      },
      avatarColors: ['#28b5c1', '#1e9aa5', '#ec4899', '#f59e0b'],
      avatarInitials: ['JD', 'SL', 'MK', 'AP'],
      contactInfo: [
        { label: 'Email Us', value: 'hello@dropone.com', icon: 'fa-solid fa-envelope', bg: '#e6f7f9' },
        { label: 'Call Us', value: '+86 755 1234 5678', icon: 'fa-solid fa-phone', bg: '#f0fdf4' },
        { label: 'WhatsApp', value: '+86 138 0000 0000', icon: 'fa-brands fa-whatsapp', bg: '#dcfce7' },
        { label: 'WeChat', value: 'FULFILLDROP_Official', icon: 'fa-brands fa-weixin', bg: '#fef3c7' },
      ],
      offices: [
        { flag: '🇨🇳', city: 'Shenzhen (HQ)', address: 'Nanshan District, Shenzhen, China' },
        { flag: '🇺🇸', city: 'Los Angeles', address: '123 Warehouse Blvd, CA 90001' },
        { flag: '🇬🇧', city: 'London', address: '456 Fulfillment Lane, UK' },
      ],
      businessHours: [
        { day: 'Monday – Friday', time: '9:00 AM – 6:00 PM (GMT+8)' },
        { day: 'Saturday', time: '10:00 AM – 4:00 PM (GMT+8)' },
        { day: 'Sunday', time: 'Closed' },
      ],
      locations: [
        { city: 'Shenzhen, China', address: 'Nanshan District — Main Warehouse & HQ', bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', tags: ['Warehouse', 'HQ', 'QC Lab'] },
        { city: 'Guangzhou, China', address: 'Baiyun District — Secondary Warehouse', bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', tags: ['Warehouse', 'Logistics'] },
        { city: 'Los Angeles, USA', address: 'CA 90001 — US Fulfillment Center', bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', tags: ['Warehouse', 'Returns'] },
        { city: 'London, UK', address: 'Unit 7, London — EU Distribution', bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', tags: ['Warehouse', 'EU Shipping'] },
      ]
    }
  },
  methods: {
    changeLang(lang) {
      this.currentLang = lang
    },
    async handleSubmit() {
      this.submitting = true
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500))
      this.submitting = false
      this.submitSuccess = true
      this.form = { name: '', email: '', company: '', phone: '', service: '', message: '' }
      setTimeout(() => { this.submitSuccess = false }, 5000)
    }
  }
}
</script>

<style scoped>
.contact-page {
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}

/* Hero */
.contact-hero {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  padding: 100px 2rem 60px;
  color: #fff;
}
.contact-hero-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
}
.contact-hero-text {
  padding-right: 20px;
}
.section-eyebrow {
  display: inline-block;
  background: rgba(40,181,193,0.25);
  color: #a5b4fc;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 5px 14px;
  border-radius: 20px;
  margin-bottom: 16px;
}
.contact-hero-title {
  font-size: 2.6rem;
  font-weight: 800;
  line-height: 1.15;
  margin: 0 0 16px;
}
.contact-hero-sub {
  font-size: 1.02rem;
  opacity: 0.82;
  line-height: 1.65;
}

/* Visual Card */
.contact-visual-card {
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 20px;
  padding: 32px;
  backdrop-filter: blur(8px);
}
.visual-avatar-group {
  display: flex;
  margin-bottom: 16px;
}
.visual-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  font-weight: 700;
  color: #fff;
  border: 3px solid #0f172a;
  margin-left: -10px;
}
.visual-avatar:first-child { margin-left: 0; }
.visual-caption {
  font-size: 0.92rem;
  color: rgba(255,255,255,0.7);
  margin-bottom: 20px;
}
.visual-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.visual-stat {
  background: rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 14px;
  text-align: center;
}
.visual-stat-num {
  display: block;
  font-size: 1.3rem;
  font-weight: 800;
  color: #a5b4fc;
}
.visual-stat-label {
  font-size: 0.72rem;
  color: rgba(255,255,255,0.5);
}

/* Main Contact Section */
.contact-main {
  padding: 60px 2rem;
  background: #f8fafc;
}
.contact-main-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 36px;
  align-items: start;
}

/* Form */
.contact-form-wrapper {
  background: #fff;
  border-radius: 18px;
  padding: 36px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.06);
  border: 1px solid #f1f5f9;
}
.form-header h2 {
  font-size: 1.5rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 6px;
}
.form-header p {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0 0 24px;
}
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.form-group {
  margin-bottom: 18px;
}
.form-group label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}
.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 11px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #1e293b;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-family: inherit;
}
.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #28b5c1;
  box-shadow: 0 0 0 3px rgba(40,181,193,0.1);
}
.form-group textarea { resize: vertical; }
.form-submit {
  background: linear-gradient(135deg, #28b5c1, #1e9aa5);
  color: #fff;
  padding: 13px 28px;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: transform 0.15s, box-shadow 0.15s;
  box-shadow: 0 4px 16px rgba(40,181,193,0.3);
}
.form-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40,181,193,0.4);
}
.form-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.form-success {
  margin-top: 14px;
  color: #10b981;
  font-weight: 600;
  font-size: 0.9rem;
}

/* Info Sidebar */
.contact-info-sidebar {
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: sticky;
  top: 88px;
}
.info-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
  border: 1px solid #f1f5f9;
}
.info-card h3 {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 16px;
}
.info-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f8fafc;
}
.info-item:last-child { border-bottom: none; }
.info-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  color: #28b5c1;
  flex-shrink: 0;
}
.info-label {
  font-size: 0.72rem;
  color: #94a3b8;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.info-value {
  font-size: 0.88rem;
  font-weight: 600;
  color: #1e293b;
}

/* Office */
.office-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f8fafc;
}
.office-card:last-child { border-bottom: none; }
.office-flag {
  font-size: 1.5rem;
  flex-shrink: 0;
  margin-top: 2px;
}
.office-city {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 2px;
}
.office-address {
  font-size: 0.78rem;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
}

/* Hours */
.hours-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f8fafc;
  font-size: 0.85rem;
}
.hours-day { color: #475569; font-weight: 500; }
.hours-time { color: #1e293b; font-weight: 600; }
.hours-note {
  margin-top: 12px;
  font-size: 0.78rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;
}
.hours-note i { color: #28b5c1; }

/* Locations */
.locations-section {
  padding: 60px 2rem;
  background: #fff;
}
.locations-inner {
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
}
.section-header { margin-bottom: 40px; }
.section-title {
  font-size: 1.9rem;
  font-weight: 800;
  color: #1e293b;
  margin: 12px 0 14px;
}
.section-desc {
  font-size: 0.95rem;
  color: #64748b;
  max-width: 520px;
  margin: 0 auto;
  line-height: 1.6;
}
.locations-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  text-align: left;
}
.location-card {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  border: 1px solid #f1f5f9;
  transition: transform 0.2s, box-shadow 0.2s;
}
.location-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.1);
}
.location-map {
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.location-pin {
  color: #fff;
  font-size: 1.8rem;
  animation: bounce 2s infinite;
}
@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}
.location-body {
  padding: 14px;
}
.location-city {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
}
.location-address {
  font-size: 0.78rem;
  color: #64748b;
  margin: 0 0 10px;
  line-height: 1.4;
}
.location-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.location-tag {
  background: #f1f5f9;
  color: #475569;
  font-size: 0.68rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
}

/* FAQ Teaser */
.contact-faq {
  padding: 60px 2rem;
  background: linear-gradient(135deg, #28b5c1 0%, #1e9aa5 100%);
  text-align: center;
  color: #fff;
}
.contact-faq h2 {
  font-size: 1.6rem;
  font-weight: 800;
  margin: 0 0 10px;
}
.contact-faq p {
  font-size: 0.95rem;
  opacity: 0.85;
  margin: 0 0 20px;
}
.btn-outline-dark {
  background: #fff;
  color: #28b5c1;
  padding: 11px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.9rem;
  text-decoration: none;
  display: inline-block;
  transition: transform 0.15s, box-shadow 0.15s;
}
.btn-outline-dark:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255,255,255,0.25);
}

/* Responsive */
@media (max-width: 1023px) {
  .contact-hero-inner { grid-template-columns: 1fr; text-align: center; }
  .contact-main-inner { grid-template-columns: 1fr; }
  .contact-info-sidebar { position: static; }
  .locations-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  .contact-hero-title { font-size: 2rem; }
  .form-row { grid-template-columns: 1fr; }
  .locations-grid { grid-template-columns: 1fr; }
}
</style>
